import { Table } from "flowbite-react";
import { ItemInput } from "../../../../@types";
import React, { useEffect, useState } from "react";
import ServiceLineItem from "./ServiceLineItem";
import PlusBoxIcon from "../../../../assets/icons/PlusBoxIcon";
import { ServiceLineItemMobile } from "./ServiceLineItemMobile";
import { useTranslation } from "react-i18next";
import { useSubscription } from "../../../../context/SubscriptionProvider";
import { ModuleName } from "../../../../types/subscription";

export interface TableColumn {
  toLowerCase(): string;
  label: string;
  key: keyof ItemInput;
}

export interface Props {
  columns: TableColumn[];
  items: ItemInput[];
  isSearchInput: boolean;
  addItem: () => void;
  removeItem: (index: any) => void;
  updateCell: (index: number, field: string, value: string | number) => void;
  updateRow: (index: number, item: ItemInput) => void;
  isImportedToInvoice?: boolean;
  selectedCurrencyLabel: string;
  handleClearErrors: () => void;
  isInvoice: boolean;
  currency: string;
  exchangeRate: number;
  handleAdd: () => void;
  serviceType?: "time-based" | "session-based";
}

const ServiceLineItemTable: React.FC<Props> = ({
  columns,
  items,
  isSearchInput = false,
  addItem,
  removeItem,
  updateCell,
  updateRow,
  isImportedToInvoice,
  selectedCurrencyLabel,
  isInvoice = false,
  handleClearErrors,
  currency,
  exchangeRate,
  handleAdd,
  serviceType,
}) => {
  const { SubscriptionGuard } = useSubscription();
  const [errors, setErrors] = React.useState<string[][]>(items.map(() => []));
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { t } = useTranslation("invoices");
  const [isMobileTableExpanded, setIsMobileTableExpanded] = useState(true);
  const [isMiniCardVisible, setIsMiniCardVisible] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver((mutationsList, observer) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          const tableElement = document.querySelector(
            '[data-testid="table-element"]',
          ) as HTMLElement;
          if (tableElement) {
            tableElement.style.overflowX = "visible";
            observer.disconnect();
          }
        }
      }
    });

    observer.observe(document, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const clearErrorsForIndex = (index: number) => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = [];
      return newErrors;
    });
  };

  const updateRowAndValidate = (index: number, item: ItemInput) => {
    updateRow(index, item);
    clearErrorsForIndex(index);
  };

  // Function to update error state
  const updateError = (index: number, error: string[]) => {
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = error;
      return newErrors;
    });
  };

  const validateItems = (items: ItemInput[]) => {
    const newErrors = items.map((item) => {
      const fields: string[] = [];
      if (!item.name) fields.push("name");
      if (!item.duration) fields.push("duration");
      if (!item.price || item.price < 0) fields.push("price");
      return fields;
    });
    setErrors(newErrors);
    return items.every((item) => {
      return !(
        !item?.name ||
        !item?.duration ||
        !item?.price ||
        item.price < 0
      );
    });
  };

  const handleAddItem = () => {
    if (validateItems(items)) {
      setErrors([...errors, []]);
      addItem();
    }
  };

  const handleInfoCircleHover = () => {
    setIsMiniCardVisible(true);
  };

  const handleInfoCircleMouseLeave = () => {
    setIsMiniCardVisible(false);
  };
  return (
    <>
      <div>
        <Table className="w-full ">
          {isMobile ? null : (
            <Table.Head className="">
              {(serviceType === "session-based"
                ? columns.filter(
                    (col) => col.key === "name" || col.key === "serviceCharge",
                  )
                : columns
              ).map((column, index) => (
                <Table.HeadCell key={index} className="">
                  {column.label}
                </Table.HeadCell>
              ))}
            </Table.Head>
          )}
          <br />
          <Table.Body className="divide-y">
            {items.map((item, index) => (
              <React.Fragment key={`${item.id}-${index}`}>
                {/* {isMobile ? (
                  <ServiceLineItemMobile
                    key={`${item.id}-${index}`}
                    index={index}
                    isSearchInput={isSearchInput}
                    item={items[index]}
                    removeItem={removeItem}
                    updateCell={updateCell}
                    updateRow={updateRowAndValidate}
                    errors={errors[index] || []}
                    updateError={updateError}
                    expanded={expandedTableIndex === index}
                    setExpanded={setExpandedTableIndex}
                    selectedCurrencyLabel={selectedCurrencyLabel}
                    currency={currency}
                    exchangeRate={exchangeRate}
                  />
                ) : ( */}
                <ServiceLineItem
                  key={`${item.id}-${index}`}
                  index={index}
                  columns={columns}
                  isSearchInput={isSearchInput}
                  item={items[index]}
                  removeItem={removeItem}
                  updateCell={updateCell}
                  updateRow={updateRowAndValidate}
                  errors={errors[index] || []}
                  updateError={updateError}
                  isInvoice={isInvoice}
                  selectedCurrencyLabel={selectedCurrencyLabel}
                  items={[]}
                  currency={currency}
                  exchangeRate={exchangeRate}
                  serviceType={serviceType}
                />
                {/* )} */}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>

        <div
          className={`flex  items-center mr-20 mt-6  ${!isInvoice && "w-full items-end justify-end"} ${isInvoice && "justify-between"}`}
        >
          {isInvoice && (
            <SubscriptionGuard
              createTitle="Add New Service"
              outlined={true}
              isListPage
              module={"services" as ModuleName}
            >
              <div>
                <div className="  hidden lg:block md:block ">
                  <span
                    onClick={handleAdd}
                    className="flex items-center text-[#044E97] font-medium cursor-pointer "
                  >
                    {t("addNewService")}
                  </span>
                </div>

                <div className="absolute mt-[-1em] lg:hidden md:hidden">
                  <span
                    onClick={handleAdd}
                    className="flex items-center text-[#044E97] text-[10px] font-medium cursor-pointer border border-[#044E97] w-[120px] py-2 px-[1em] 320:ml-[7em] 375:ml-[3em] 360:ml-[4em] rounded-md"
                  >
                    {t("addNewService")}
                  </span>
                </div>
              </div>
            </SubscriptionGuard>
          )}
          <span
            onClick={handleAddItem}
            className="flex text-[#044E97] justify-end font-medium cursor-pointer items-center"
          >
            {t("addLine")}
            <div className="ml-2">
              <PlusBoxIcon />
            </div>
          </span>
        </div>
      </div>
    </>
  );
};

export { ServiceLineItemTable };
